import { SerializedError } from '@reduxjs/toolkit';

export const DEFAULT_PAGE_SIZE = 12;

// --- "Mitie Duty Manager Informed" (critical incidents only) ---

export type MitieDutyManagerInformed = 'contact_made' | 'voicemail_left' | 'tbc';

// Mirrors App\Enums\MitieDutyManagerInformed on the API — keep in step.
export const MITIE_DUTY_MANAGER_INFORMED_OPTIONS: { label: string; value: MitieDutyManagerInformed }[] = [
    { label: 'Contact Made', value: 'contact_made' },
    { label: 'Voicemail Left', value: 'voicemail_left' },
    { label: 'TBC', value: 'tbc' },
];

// --- Filter option types (from filters response) ---

export interface IncidentStatusOption {
    id: number;
    name: string;
}

export interface IncidentPriorityOption {
    id: number;
    name: string;
}

export interface IncidentCategoryOption {
    id: number;
    name: string;
    showRemedyReference: boolean;
    showKhcReference: boolean;
    showMaximoReference: boolean;
    showConceptReference: boolean;
    showPoliceReference: boolean;
    requireCheckInOut: boolean;
    defaultPriorityId: number | null;
    visibleRetail: boolean;
    isCriticalCategory: boolean;
    // Empty array = all active priorities allowed.
    priorityIds: number[];
}

export interface IncidentRemedyOption {
    id: number;
    reference: string;
}

export interface IncidentSiteOption {
    id: number;
    name: string;
    // Site type name, e.g. "DC" — a display value an admin can rename.
    type?: string;
    // Site type's guarding type ('retail' / 'non_retail'), which is what the
    // retail-only UI rules key off. See pages/Incidents/retailSite.ts.
    guardingType?: string | null;
}

// --- Nested incident object types ---

export interface IncidentSite {
    id: number;
    // Encrypted id used to navigate to the site details page (/site/{encryptedId}).
    encryptedId?: string;
    name: string;
    type?: string | null;
}

export interface IncidentLoggedByUser {
    id: number;
    name: string;
}

export interface IncidentProblemCategory {
    id: number;
    name: string;
}

export interface IncidentStatusObj {
    id: number;
    name: string;
}

export interface IncidentPriorityObj {
    id: number;
    name: string;
}

// --- Diary entry types ---

export interface DiaryEntryMedia {
    id: number;
    filename: string;
    originalFilename: string;
    url: string;
}

export interface DiaryEntryUserRole {
    name: string;
    accessLevel: string;
}

export interface DiaryEntryUser {
    id: number;
    name: string;
    email?: string;
    role?: DiaryEntryUserRole;
}

export interface DiaryEntry {
    id: number;
    user: DiaryEntryUser;
    text: string;
    entryOccurredAt: string | null;
    sentToDistribution: boolean;
    media: DiaryEntryMedia[];
    createdAt: string | null;
    updatedAt: string | null;
}

// --- Incident image types ---

export interface IncidentImage {
    id: number;
    description: string | null;
    uploadDate: string | null;
    uploadedBy: string | null;
    url: string;
    filename: string;
}

// --- Main incident type ---

export interface Incident {
    id: number;
    site: IncidentSite;
    loggedByUser: IncidentLoggedByUser | null;
    incidentProblemCategory: IncidentProblemCategory;
    incidentStatus: IncidentStatusObj;
    incidentPriority: IncidentPriorityObj;
    remedyReference: string | null;
    maximoReference: string | null;
    khcReference: string | null;
    policeReference: string | null;
    conceptReference: string | null;
    isCritical: boolean;
    // Answer to "Mitie Duty Manager Informed", asked on critical incidents only.
    mitieDutyManagerInformed: MitieDutyManagerInformed | null;
    mitieDutyManagerInformedLabel: string | null;
    policeAssigned: boolean;
    reason: string | null;
    description: string;
    checkedInAt: string | null;
    checkedOutAt: string | null;
    resolutionDetails: string | null;
    rootCause: string | null;
    completedByUser: { id: number; name: string } | null;
    completedAt: string | null;
    dateLogged: string;
    diaryEntriesCount: number;
    diaryEntries: DiaryEntry[] | null;
    images: IncidentImage[];
    createdAt: string;
    updatedAt: string;
}

// --- Summary counts ---

export interface IncidentSummary {
    new: number;
    active: number;
    critical: number;
    completed: number;
}

// --- Filters data ---

export interface IncidentFiltersData {
    statuses: IncidentStatusOption[];
    priorities: IncidentPriorityOption[];
    problemCategories: IncidentCategoryOption[];
    remedyReferences: Array<{ reference: string }>;
    sites: IncidentSiteOption[];
}

// --- Pagination ---

export interface PaginationProps {
    currentPage: number;
    pageSize: number;
    total: number;
}

// --- API response types ---

export interface IncidentsResponseData {
    summary?: IncidentSummary;
    filters: IncidentFiltersData;
    incidents: {
        data: Incident[];
        links: {
            first: string;
            last: string;
            prev: string | null;
            next: string | null;
        };
        meta: {
            current_page: number;
            from: number;
            last_page: number;
            path: string;
            per_page: number;
            to: number;
            total: number;
        };
    };
}

export interface IncidentsResponseProps {
    data: IncidentsResponseData;
}

// --- Filter payload for search/filter thunk ---

export interface IncidentFilterPayload {
    page?: number;
    pageSize?: number;
    q?: string;
    status?: Array<string> | string;
    priority?: Array<string> | string;
    problemCategory?: Array<string> | string;
    remedy?: Array<string> | string;
    site?: Array<string> | string;
    siteId?: string | number;
    is_critical?: '1' | '0';
    dateLoggedFrom?: string;
    dateLoggedTo?: string;
    sort?: string | null;
    sortField?: string | null;
}

// --- Export payload ---

export type ExportKey = 'csv' | 'email';

export interface IncidentExportPayload extends Omit<IncidentFilterPayload, 'page' | 'pageSize'> {
    exportType: ExportKey;
}

export type IncidentEmailExportPayload = Omit<IncidentFilterPayload, 'page' | 'pageSize'>;

// --- Create/Update payloads ---

export interface IncidentCreatePayload {
    site_id: number;
    incident_problem_category_id: number;
    // Null for retail incidents, which do not require a priority.
    incident_priority_id: number | null;
    incident_status_id: number;
    description: string;
    is_critical: boolean;
    mitie_duty_manager_informed?: MitieDutyManagerInformed | null;
    remedy_reference?: string | null;
    maximo_reference?: string | null;
    khc_reference?: string | null;
    concept_reference?: string | null;
    police_reference?: string | null;
    police_assigned?: boolean | null;
    reason?: string | null;
}

export interface IncidentUpdatePayload {
    incident_problem_category_id?: number | undefined;
    // Null for retail incidents, which do not require a priority.
    incident_priority_id?: number | null;
    incident_status_id: number;
    description?: string | undefined;
    is_critical?: boolean | undefined;
    mitie_duty_manager_informed?: MitieDutyManagerInformed | null;
    remedy_reference?: string | null;
    maximo_reference?: string | null;
    khc_reference?: string | null;
    concept_reference?: string | null;
    police_reference?: string | null;
    police_assigned?: boolean | null;
    reason?: string | null;
    checked_in_at?: string | null;
    checked_out_at?: string | null;
    // Supplied only when the status is being changed to Closed, to complete the
    // incident in the same request.
    root_cause?: string | null;
    resolution_details?: string | null;
    // Whether this edit is reported to the distribution list. Omitting it makes
    // the API notify, so the edit form always sends it explicitly.
    send_to_distribution?: boolean;
}

// --- Complete payload ---

export interface IncidentCompletePayload {
    root_cause: string;
    resolution_details: string;
}

// --- Reopen payload ---

export interface IncidentReopenPayload {
    incident_status_id: number;
}

export interface DiaryEntryCreatePayload {
    incidentId: number;
    formData: FormData;
}

export interface DiaryEntryUpdatePayload {
    incidentId: number;
    diaryEntryId: number;
    text: string;
    entryOccurredAt: string | null;
}

// A rendered distribution email preview — returned by both the diary-entry and
// the incident-edit preview endpoints, which share one response shape.
export interface IncidentEmailPreview {
    html: string;
    subject: string;
    recipients: string[];
}

// --- Redux state ---

export interface IncidentsState {
    error: string | SerializedError | null;
    isFetching: boolean;
    incidents: Incident[];
    pagination: PaginationProps;
    filterData: IncidentFiltersData;
    summaryData?: IncidentSummary;
    isExporting: boolean;
    incidentDetail: Incident | null;
    incidentDetailError: string | null;
}
